import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  signal,
  ViewEncapsulation,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from '@coin/shared/auth/data-access';
import { FirstVisitService, UserMenuComponent, LazyFaqMenuComponent } from '@coin/shared/auth/feature';
import { CmsTranslationService, FaqService, StorageService } from '@coin/shared/core/data-access';
import { ResponsivenessService } from '@coin/shared/core/feature-state';
import { ContentLanguage, FrontendType, StorageKey } from '@coin/shared/core/util-enums';
import { environment } from '@coin/shared/core/util-environments';
import { GlobalEventsService, helpDeepLinkOpened$, UpdateNotificationsService } from '@coin/shared/core/util-helpers';
import { FaqItem } from '@coin/shared/core/util-models';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { iif, Observable, of } from 'rxjs';
import { delay, filter, finalize, skip, switchMap, tap } from 'rxjs/operators';
import { OpenFaqService } from '../../shared/services/open-faq.service';
import { MatIcon } from '@angular/material/icon';
import { MatBadge } from '@angular/material/badge';
import { AsyncPipe } from '@angular/common';
import { FeatureFlagDirective } from '@coin/shared/core/feature-directives';
import { UpdateNotifierComponent, V2ButtonComponent } from '@coin/shared/core/ui-storybook';
import { ChatBotComponent } from '@coin/chat-bot-feature';

@Component({
  selector: 'customer-app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FeatureFlagDirective,
    MatIcon,
    UserMenuComponent,
    UpdateNotifierComponent,
    ChatBotComponent,
    V2ButtonComponent,
    MatBadge,
    LazyFaqMenuComponent,
    AsyncPipe,
    TranslatePipe
  ]
})
export class HeaderComponent implements OnInit {
  private readonly router = inject(Router);
  authService = inject(AuthService);
  private readonly translate = inject(TranslateService);
  private readonly eRef = inject(ElementRef);
  private readonly guidedTour = inject(FirstVisitService);
  private readonly openFaqService = inject(OpenFaqService);
  private readonly storageService = inject(StorageService);
  private readonly cmsTranslationService = inject(CmsTranslationService);
  private readonly faqService = inject(FaqService);
  responsive = inject(ResponsivenessService);
  private readonly events = inject(GlobalEventsService);
  private readonly destroyRef = inject(DestroyRef);
  protected updateNotifications = inject(UpdateNotificationsService);
  private readonly helpDeepLinkOpened$ = helpDeepLinkOpened$();

  public isAuthenticated$: Observable<boolean>;
  private faqOverride = false;

  public isApp = signal(false);
  public showFaq = signal(false);
  public isHelpDataLoading = signal(false);
  public languageSelected = signal(ContentLanguage.ENGLISH);
  public helpData = signal<{ [lang in ContentLanguage]?: FaqItem[] }>({});
  public deactivated = signal(false);
  public totalNewFlags = signal(0);

  clickout = (event: MouseEvent): void => {
    if (!this.eRef.nativeElement.contains(event.target)) {
      if (this.showFaq() && !this.faqOverride) {
        this.toggleFaq();
      }
    }
  };

  constructor() {
    this.isApp.set(environment.device !== 'desktop');
  }

  get currentEnvironment(): typeof environment {
    return environment;
  }

  ngOnInit(): void {
    this.isAuthenticated$ = this.authService.isAuthenticated$;
    this.openFaqService.openClose$
      .pipe(delay(10), takeUntilDestroyed(this.destroyRef))
      .subscribe((faqstate: boolean) => {
        this.showFaq.set(faqstate);
        this.faqOverride = faqstate;
      });
    this.getTranslations();

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(route => {
      if (route instanceof NavigationEnd) {
        this.deactivated.set(route.url === '/fallback');
      }
    });

    this.events.listen('click').pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.clickout);
    this.openFaqOnDeepLink();
  }

  private openFaqOnDeepLink(): void {
    this.helpDeepLinkOpened$
      .pipe(
        filter(() => !this.showFaq()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.toggleFaq());
  }

  private getTranslations(): void {
    this.cmsTranslationService
      .getTranslationConfigFile(FrontendType.customer)
      .pipe(
        filter(selectableLangs => !!selectableLangs),
        switchMap(() => this.storageService.getAsync(StorageKey.LANGUAGE)),
        filter(lang => !!lang),
        tap(lang => {
          this.languageSelected.set(lang);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.translate.onLangChange.pipe(skip(1), takeUntilDestroyed(this.destroyRef)).subscribe(lang => {
      this.languageSelected.set(lang.lang as ContentLanguage);
      setTimeout(() => {
        this.getHelpData();
      }, 600);
    });
  }

  async getHelpData(): Promise<void> {
    if (!this.helpData()[this.languageSelected()]) {
      this.isHelpDataLoading.set(true);
      const filterValue = 'coin';
      this.faqService
        .getFaq(`help_${this.languageSelected()}_publish.json`, filterValue)
        .pipe(
          switchMap(data => iif(() => !data, this.faqService.getFaq(`help_en_publish.json`, filterValue), of(data))),
          filter(helpItem => !!helpItem),
          tap(helpItem => {
            this.searchForNewFlagItems(helpItem);
            this.helpData.update(helpData => {
              helpData[this.languageSelected()] = helpItem;
              return { ...helpData };
            });
          }),
          finalize(() => {
            this.isHelpDataLoading.set(false);
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    }
  }

  private searchForNewFlagItems(helpItems: FaqItem[]): void {
    this.totalNewFlags.set(0);
    for (const item of helpItems) {
      item.newFlagCount = this.checkItemsForFlag(item);
      this.totalNewFlags.update(flags => flags + item.newFlagCount);
    }
  }

  private checkItemsForFlag(item: FaqItem): number {
    if (item && item.content && !item.isAnswer) {
      let contentNewFlagCount = 0;
      if (item.newFlagUntil) {
        contentNewFlagCount += 1;
      } else {
        for (const itm of item.content) {
          itm.newFlagCount = this.checkItemsForFlag(itm);
          contentNewFlagCount += itm.newFlagCount;
        }
      }
      return contentNewFlagCount;
    }
    if (item && item.content && item.isAnswer) {
      if (item.newFlagUntil) {
        const timeUntilEndNewFlag =
          new Date(item.newFlagUntil).getTime() - new Date(new Date().setHours(0, 0, 0, 0)).getTime();
        if (timeUntilEndNewFlag > 0) {
          return 1;
        }
      }
    }
    return 0;
  }

  public login(): void {
    this.authService.login();
  }

  public toggleFaq(): void {
    this.showFaq.set(!this.showFaq());
    if (this.showFaq()) {
      this.getHelpData();
    }
  }

  public onOpenOtherFaqTopic(title: string): void {
    if (title === 'openGuidedTour') {
      this.storageService.remove(StorageKey.FIRST_VISIT_COMPLETED);
      this.guidedTour.reopenGuidedTour();
    } else if (title === 'helpdesk') {
      window.open(environment.helpdeskUrl, '_blank');
    }
  }
}
